import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Check, RefreshCw, Loader2, CheckCheck, FileText } from "lucide-react";
import api from "@/lib/api";
import { TopBar } from "@/pages/Dashboard";

const CHAPTER_LABEL = {
  childhood: "Childhood & family", growing_up: "Growing up", love_marriage: "Love & marriage",
  work_purpose: "Work & purpose", parenthood: "Parenthood", hard_times: "Hard times & resilience",
  joys_passions: "Joys & passions", lessons_legacy: "Lessons & legacy", messages_future: "Messages to the future",
};

export default function StoryScript() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [lines, setLines] = useState([]);
  const [pct, setPct] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/stories/${storyId}/script`);
      setLines(r.data.script?.lines || []);
      setPct(r.data.approved_pct || 0);
    } catch (e) { if (e?.response?.status === 404) navigate("/dashboard"); }
    finally { setLoading(false); }
  }, [storyId, navigate]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try { const r = await api.post(`/stories/${storyId}/script/generate`); setLines(r.data.script?.lines || []); setPct(0); toast.success("Draft narration written — review it line by line"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); }
    finally { setGenerating(false); }
  };

  const patchLine = async (id, patch) => {
    setLines((ls) => ls.map((l) => l.id === id ? { ...l, ...patch } : l));
    try { const r = await api.patch(`/stories/${storyId}/script/line/${id}`, patch); setPct(r.data.approved_pct); } catch {}
  };
  const approveAll = async () => { const r = await api.post(`/stories/${storyId}/script/approve-all`); setPct(r.data.approved_pct); setLines((ls) => ls.map((l) => ({ ...l, approved: true }))); toast.success("All lines approved"); };

  const grouped = lines.reduce((acc, l) => { (acc[l.chapter] = acc[l.chapter] || []).push(l); return acc; }, {});

  return (
    <div className="min-h-screen" data-testid="story-script">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(`/stories/${storyId}`)} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:underline"><ArrowLeft size={15} /> Back to story</button>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-semibold flex items-center gap-2"><FileText size={22} className="text-[hsl(var(--accent))]" /> Narration script</h1>
            <p className="text-[hsl(var(--muted-foreground))]">AI drafts the narration from your interviews and memories. You approve every line.</p>
          </div>
          <button data-testid="script-generate-button" onClick={generate} disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] disabled:opacity-60">
            {generating ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} {lines.length ? "Regenerate" : "Generate draft"}
          </button>
        </div>

        {lines.length > 0 && (
          <div className="mt-5 sticky top-2 z-10 rounded-xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))]/90 backdrop-blur p-3 flex items-center gap-3">
            <div className="flex-1"><div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden"><div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} /></div></div>
            <span className="font-mono text-xs tabular-nums text-[hsl(var(--muted-foreground))]" data-testid="script-approved-pct">{pct}% approved</span>
            <button data-testid="script-approve-all" onClick={approveAll} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold"><CheckCheck size={15} /> Approve all</button>
          </div>
        )}

        {loading ? <div className="mt-6 h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />
          : lines.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-[hsl(var(--border))] p-12 text-center text-[hsl(var(--muted-foreground))]">
              <Sparkles className="mx-auto text-[hsl(var(--accent))]" />
              <p className="mt-2">No script yet. Record some interviews or add memories, then generate a draft narration.</p>
            </div>
          ) : (
            <div className="mt-6 space-y-6">
              {Object.entries(grouped).map(([ch, ls]) => (
                <div key={ch}>
                  <h2 className="font-mono text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{CHAPTER_LABEL[ch] || ch}</h2>
                  <div className="mt-2 space-y-2">
                    {ls.map((l) => (
                      <div key={l.id} data-testid={`script-line-${l.id}`} className={`rounded-xl border p-3 transition-colors ${l.approved ? "border-[hsl(var(--chapter-good))]/40 bg-[hsl(var(--chapter-good))]/5" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"}`}>
                        <textarea value={l.text} onChange={(e) => patchLine(l.id, { text: e.target.value })}
                          className="w-full bg-transparent resize-none focus:outline-none text-[hsl(var(--foreground))] font-display text-lg leading-snug" rows={2} data-testid={`script-line-text-${l.id}`} />
                        <div className="mt-1 flex justify-end">
                          <button data-testid={`script-line-approve-${l.id}`} onClick={() => patchLine(l.id, { approved: !l.approved })}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold ${l.approved ? "bg-[hsl(var(--chapter-good))]/15 text-[hsl(var(--chapter-good))]" : "border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`}>
                            <Check size={14} /> {l.approved ? "Approved" : "Approve"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
      </main>
    </div>
  );
}
